// ============================================================
// NOWGUT — Data Artikel Pusat Informasi (InfoHub)
// ------------------------------------------------------------
// Konten medis telah disesuaikan dengan rujukan ilmiah (2017–2025).
// CATATAN: Tinjau ulang bersama tenaga medis sebelum publikasi.
// Ikon TIDAK disimpan di sini (data murni) — dipetakan di komponen
// lewat field `iconKey`, agar file data bebas dari JSX.
// ============================================================

export const ARTICLES = [
  // ---------------------------------------------------------
  // 1. GI & ASD
  // ---------------------------------------------------------
  {
    id: 1,
    category: 'GI & ASD',
    iconKey: 'link',
    readMinutes: 4,
    title_id: 'Hubungan Gangguan Pencernaan dan Autisme',
    title_en: 'The Connection Between GI Disorders and Autism',
    summary_id: 'Sekitar separuh anak dengan ASD mengalami masalah pencernaan. Pahami kaitan usus–otak (gut-brain axis) dan mengapa nyeri perut sering muncul sebagai perubahan perilaku.',
    summary_en: 'Around half of children with ASD experience GI problems. Understand the gut–brain axis and why abdominal pain often shows up as behavioral change.',
    content_id: `Gangguan gastrointestinal (GI) termasuk kondisi yang paling sering dialami anak dengan Autism Spectrum Disorder (ASD). Tinjauan ilmiah menunjukkan prevalensinya jauh lebih tinggi dibanding anak pada umumnya, meskipun angka pastinya bervariasi antar penelitian.

**Seberapa Sering Terjadi?**
Estimasi prevalensi bervariasi cukup lebar antar studi karena perbedaan metode pengukuran. Sebuah meta-analisis besar memperkirakan sekitar 46–55% anak ASD mengalami setidaknya satu gejala GI — beberapa studi melaporkan angka serendah 4% hingga setinggi 97%. Yang jelas dan konsisten: anak ASD berisiko jauh lebih tinggi dibanding anak dengan perkembangan tipikal.

**Gut-Brain Axis (Sumbu Usus–Otak)**
Usus dan otak terhubung melalui jaringan saraf, hormon, dan mikrobiota yang disebut "gut-brain axis". Gangguan pada koneksi ini diduga berkaitan dengan gejala pencernaan maupun perilaku, meski mekanisme pastinya masih terus diteliti.

**Mengapa Sering "Tersembunyi"?**
Banyak anak ASD kesulitan mengomunikasikan rasa sakit. Akibatnya, nyeri perut atau ketidaknyamanan GI sering muncul sebagai perubahan perilaku — mudah marah, menarik diri, hiperaktif, atau perilaku menantang. Penelitian menemukan bahwa nyeri perut, diare, dan sembelit berkaitan dengan meningkatnya perilaku menantang pada anak ASD.

**Mengapa Pemantauan Penting?**
Karena gejalanya sering tidak terungkap secara verbal, pencatatan rutin menjadi sangat berharga. Dengan mencatat pola buang air besar, makanan, dan perubahan perilaku, caregiver dapat membantu dokter mengenali pola dan memberikan penanganan yang lebih tepat.`,
    content_en: `Gastrointestinal (GI) disorders are among the most common conditions in children with Autism Spectrum Disorder (ASD). Scientific reviews show their prevalence is far higher than in typically developing children, though exact figures vary across studies.

**How Common Is It?**
Prevalence estimates vary widely between studies due to differences in measurement. A large meta-analysis estimated that roughly 46–55% of children with ASD experience at least one GI symptom — with individual studies reporting anywhere from 4% to 97%. What is clear and consistent: children with ASD are at substantially higher risk than typically developing peers.

**The Gut-Brain Axis**
The gut and brain are connected through nerves, hormones, and gut microbiota — together called the "gut-brain axis." Disruption in this connection is thought to relate to both digestive and behavioral symptoms, though the exact mechanism is still being researched.

**Why It's Often "Hidden"**
Many children with ASD struggle to communicate pain. As a result, abdominal pain or GI discomfort often appears as behavioral change — irritability, withdrawal, hyperactivity, or challenging behavior. Research has found abdominal pain, diarrhea, and constipation are linked to increased challenging behavior in children with ASD.

**Why Monitoring Matters**
Because symptoms are often not expressed verbally, regular tracking becomes valuable. By recording bowel patterns, food, and behavioral changes, caregivers can help doctors recognize patterns and provide more appropriate care.`,
    references: [
      { label: 'Frontiers in Pediatrics (2023) — Meta-analisis prevalensi gejala GI pada ASD', url: 'https://www.frontiersin.org/journals/pediatrics/articles/10.3389/fped.2023.1120728/full' },
      { label: 'Holingue et al. (2018), Autism Research — Tinjauan prevalensi (median 46,8%)', url: 'https://pubmed.ncbi.nlm.nih.gov/28856868/' },
      { label: 'Nutrients (2022) — Systematic Review gejala GI pada ASD', url: 'https://www.mdpi.com/2072-6643/14/7/1471' },
    ],
    medicalReview: true,
  },

  // ---------------------------------------------------------
  // 2. Nutrisi
  // ---------------------------------------------------------
  {
    id: 2,
    category: 'Nutrisi',
    iconKey: 'apple',
    readMinutes: 5,
    title_id: 'Panduan Nutrisi untuk Anak ASD dengan Masalah GI',
    title_en: 'Nutrition Guide for Children with ASD and GI Problems',
    summary_id: 'Pola makan seimbang dan cukup serat dapat mendukung kesehatan pencernaan. Pelajari prinsip umum nutrisi — dan apa kata bukti terbaru soal diet bebas gluten-kasein.',
    summary_en: 'A balanced, fiber-adequate diet can support digestive health. Learn general nutrition principles — and what current evidence says about the gluten-casein-free diet.',
    content_id: `Nutrisi yang seimbang berperan penting dalam kesehatan pencernaan anak dengan ASD. Berikut panduan umum yang dapat membantu. Ingat: kebutuhan setiap anak berbeda, jadi diskusikan dengan dokter atau ahli gizi.

**Makanan yang Umumnya Dianjurkan**
- Sayuran berserat: brokoli, wortel, bayam
- Buah-buahan: pisang, apel, pir
- Protein tanpa lemak berlebih: ayam, ikan, tahu, telur
- Sumber probiotik alami: yogurt (jika tidak alergi/intoleran susu)
- Cairan cukup: air putih sesuai usia dan anjuran dokter

**Peran Serat & Cairan**
Serat dan cairan yang cukup membantu mencegah sembelit — salah satu masalah GI tersering pada anak ASD. Tingkatkan serat secara bertahap agar perut tidak kaget.

**Yang Perlu Diperhatikan**
- Makanan sangat tinggi gula olahan
- Makanan ultra-proses dengan banyak aditif
- Makanan yang memicu reaksi pada anak tertentu (catat & amati)

**Diet Bebas Gluten-Kasein (GFCF): Apa Kata Bukti?**
Diet GFCF populer di kalangan orang tua, namun penting memahami buktinya. Tinjauan sistematis dan uji acak terkontrol menyimpulkan bahwa **bukti manfaat GFCF untuk gejala inti ASD masih terbatas dan lemah.** Beberapa keluarga melaporkan perbaikan, tetapi studi berkualitas tinggi belum menunjukkan efek yang konsisten. Sebaliknya, ada kekhawatiran diet ketat ini justru dapat memicu efek samping pencernaan dan kekurangan gizi jika tidak diawasi.

Rekomendasi medis saat ini: **diet GFCF sebaiknya hanya diterapkan setelah ada diagnosis alergi atau intoleransi** terhadap gluten/kasein — bukan sebagai pengobatan ASD secara umum. Jangan memulai diet eliminasi tanpa pengawasan dokter atau ahli gizi, karena risiko kekurangan nutrisi pada anak yang sedang tumbuh.`,
    content_en: `Balanced nutrition plays an important role in the digestive health of children with ASD. Here are general guidelines that can help. Remember: every child's needs differ, so discuss with a doctor or dietitian.

**Generally Recommended Foods**
- Fiber-rich vegetables: broccoli, carrots, spinach
- Fruits: bananas, apples, pears
- Lean proteins: chicken, fish, tofu, eggs
- Natural probiotic sources: yogurt (if not dairy allergic/intolerant)
- Adequate fluids: water appropriate to age and medical advice

**The Role of Fiber & Fluids**
Adequate fiber and fluids help prevent constipation — one of the most common GI issues in children with ASD. Increase fiber gradually so the gut can adjust.

**What to Watch For**
- Foods very high in refined sugar
- Ultra-processed foods with many additives
- Foods that trigger reactions in specific children (record & observe)

**Gluten-Casein-Free (GFCF) Diet: What Does Evidence Say?**
The GFCF diet is popular among parents, but it's important to understand the evidence. Systematic reviews and randomized controlled trials conclude that **evidence for GFCF benefits on core ASD symptoms remains limited and weak.** Some families report improvement, but high-quality studies have not shown a consistent effect. On the contrary, there is concern that this restrictive diet may trigger GI side effects and nutritional deficiencies if unsupervised.

Current medical recommendation: **a GFCF diet should only be used after a diagnosis of allergy or intolerance** to gluten/casein — not as a general ASD treatment. Do not start an elimination diet without supervision from a doctor or dietitian, given the risk of nutritional deficiency in a growing child.`,
    references: [
      { label: 'González-Domenech et al. (2017) — GFCF diet RCT review (Cochrane databases)', url: 'https://pubmed.ncbi.nlm.nih.gov/28612113/' },
      { label: 'Nutrients (2021) — GFCF systematic review & meta-analysis (GRADE)', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7912271/' },
      { label: 'Piwowarczyk et al. — GFCF review, rekomendasi pasca-diagnosis alergi', url: 'https://pubmed.ncbi.nlm.nih.gov/24789114/' },
    ],
    medicalReview: true,
  },

  // ---------------------------------------------------------
  // 3. Bristol Chart (dewasa/umum) — DIKOREKSI
  // ---------------------------------------------------------
  {
    id: 3,
    category: 'Bristol Chart',
    iconKey: 'chart',
    readMinutes: 4,
    title_id: 'Memahami Bristol Stool Chart',
    title_en: 'Understanding the Bristol Stool Chart',
    summary_id: 'Bristol Stool Chart mengklasifikasikan bentuk feses ke dalam 7 tipe. Pelajari arti tiap tipe, warna feses, dan kapan harus waspada.',
    summary_en: 'The Bristol Stool Chart classifies stool into 7 types. Learn what each type means, stool colors, and when to be alert.',
    content_id: `Bristol Stool Chart adalah skala medis yang dikembangkan oleh Lewis & Heaton (1997) untuk mengklasifikasikan bentuk feses ke dalam 7 tipe. Skala ini membantu menilai apakah pencernaan terlalu lambat (sembelit) atau terlalu cepat (diare).

**7 Tipe Bentuk Feses**
**Tipe 1** — Gumpalan keras terpisah seperti kacang, sulit dikeluarkan → Sembelit (berat)
**Tipe 2** — Berbentuk sosis tetapi menggumpal → Sembelit (ringan)
**Tipe 3** — Seperti sosis dengan retakan di permukaan → Normal
**Tipe 4** — Seperti sosis atau ular, halus dan lembut → Normal / ideal
**Tipe 5** — Gumpalan lunak bertepi jelas, mudah dikeluarkan → Cenderung kurang serat
**Tipe 6** — Potongan lembek bertepi tidak rata → Diare (ringan)
**Tipe 7** — Cair sepenuhnya tanpa bagian padat → Diare (berat)

**Cara Membaca**
Tipe 3, 4, dan 5 umumnya dianggap dalam rentang normal, dengan **Tipe 4 sebagai yang paling ideal.** Tipe 1–2 menandakan sembelit; Tipe 6–7 menandakan diare. Tipe 5 sering dikaitkan dengan asupan serat yang kurang, bukan diare.

**Warna Feses**
- Coklat/Kuning kecoklatan: Umumnya normal
- Hijau: Bisa karena makanan hijau atau transit cepat — biasanya tidak berbahaya
- Hitam pekat: Perlu perhatian medis (bisa menandakan perdarahan saluran cerna atas)
- Pucat/Putih seperti dempul: Perlu perhatian medis
- Merah (darah segar): Perlu perhatian medis

**Catatan untuk Anak**
Untuk anak, tersedia versi khusus yang lebih mudah digunakan (lihat artikel "Bristol Chart Versi Anak"). Bila ragu, foto feses dan tunjukkan ke dokter.`,
    content_en: `The Bristol Stool Chart is a medical scale developed by Lewis & Heaton (1997) to classify stool into 7 types. It helps assess whether digestion is too slow (constipation) or too fast (diarrhea).

**The 7 Stool Types**
**Type 1** — Separate hard lumps like nuts, hard to pass → Constipation (severe)
**Type 2** — Sausage-shaped but lumpy → Constipation (mild)
**Type 3** — Like a sausage with cracks on the surface → Normal
**Type 4** — Like a sausage or snake, smooth and soft → Normal / ideal
**Type 5** — Soft blobs with clear-cut edges, easy to pass → Tends toward low fiber
**Type 6** — Mushy pieces with ragged edges → Diarrhea (mild)
**Type 7** — Entirely liquid with no solid pieces → Diarrhea (severe)

**How to Read It**
Types 3, 4, and 5 are generally considered within the normal range, with **Type 4 being the most ideal.** Types 1–2 indicate constipation; Types 6–7 indicate diarrhea. Type 5 is often linked to insufficient fiber intake rather than diarrhea.

**Stool Colors**
- Brown/Yellow-brown: Generally normal
- Green: May be from green foods or fast transit — usually harmless
- Deep black: Needs medical attention (may indicate upper GI bleeding)
- Pale/Putty-white: Needs medical attention
- Red (fresh blood): Needs medical attention

**Note for Children**
A child-friendly version is available that is easier to use (see "Bristol Chart for Children"). When in doubt, photograph the stool and show your doctor.`,
    references: [
      { label: 'Lewis SJ, Heaton KW (1997), Scand J Gastroenterol — skala asli', url: 'https://pubmed.ncbi.nlm.nih.gov/9299672/' },
      { label: 'Continence Health Australia — Bristol Stool Chart (rujukan klinis)', url: 'https://www.continence.org.au/about-incontinence/bowel-incontinence/bristol-stool-chart/' },
    ],
    medicalReview: true,
  },

  // ---------------------------------------------------------
  // 4. Tips Praktis
  // ---------------------------------------------------------
  {
    id: 4,
    category: 'Tips',
    iconKey: 'bulb',
    readMinutes: 4,
    title_id: 'Tips Praktis untuk Caregiver',
    title_en: 'Practical Tips for Caregivers',
    summary_id: 'Strategi sehari-hari untuk membantu mengelola kesehatan pencernaan anak ASD: rutinitas, pemantauan gejala, dan komunikasi dengan tim medis.',
    summary_en: 'Everyday strategies to help manage the GI health of a child with ASD: routines, symptom monitoring, and communicating with the medical team.',
    content_id: `Merawat anak ASD dengan masalah pencernaan membutuhkan kesabaran dan strategi. Berikut tips yang dapat membantu.

**Membangun Rutinitas**
- Tetapkan jadwal makan yang konsisten setiap hari
- Sediakan waktu tenang untuk BAB di jam yang sama (mis. setelah sarapan)
- Pastikan anak cukup bergerak setiap hari
- Batasi distraksi (termasuk layar) saat makan

**Memantau Gejala**
- Catat perubahan feses: warna, bentuk (gunakan Bristol Chart), frekuensi
- Perhatikan perubahan perilaku yang mungkin berkaitan dengan nyeri
- Foto feses bila ada perubahan mencurigakan untuk ditunjukkan ke dokter
- Catat makanan yang dimakan beserta reaksinya

**Komunikasi dengan Tim Medis**
- Bawa catatan pemantauan saat kontrol
- Tanyakan kemungkinan alergi atau intoleransi makanan
- Diskusikan apakah suplemen tertentu sesuai untuk anak Anda
- Minta rujukan ke dokter spesialis anak (gastroenterologi) bila diperlukan

**Mengelola Stres & Lingkungan**
- Perubahan rutinitas dapat memperberat gejala — buat transisi sehalus mungkin
- Ciptakan suasana makan yang tenang dan tidak memaksa
- Jika anak menolak suatu makanan, cari alternatif bergizi yang setara

Ingat: Anda mengenal anak Anda lebih dari siapa pun. Catatan harian Anda adalah informasi berharga bagi dokter.`,
    content_en: `Caring for a child with ASD and GI problems takes patience and strategy. Here are tips that can help.

**Building Routines**
- Set a consistent meal schedule each day
- Provide calm bathroom time at the same hour (e.g., after breakfast)
- Ensure the child moves enough each day
- Limit distractions (including screens) during meals

**Monitoring Symptoms**
- Record stool changes: color, form (use the Bristol Chart), frequency
- Watch for behavioral changes possibly related to pain
- Photograph stool if there are suspicious changes to show the doctor
- Record foods eaten along with reactions

**Communicating with the Medical Team**
- Bring monitoring logs to appointments
- Ask about possible food allergies or intolerances
- Discuss whether specific supplements suit your child
- Request a referral to a pediatric (gastroenterology) specialist if needed

**Managing Stress & Environment**
- Routine changes can worsen symptoms — make transitions as smooth as possible
- Create a calm, non-forcing mealtime atmosphere
- If your child refuses a food, find an equally nutritious alternative

Remember: You know your child better than anyone. Your daily log is valuable information for the doctor.`,
    references: [
      { label: 'Nutrients (2022) — GI symptoms & behavior in ASD (konteks pemantauan)', url: 'https://www.mdpi.com/2072-6643/14/7/1471' },
    ],
    medicalReview: false,
  },

  // ---------------------------------------------------------
  // 5. Bristol Chart Versi Anak (mBSFS-C) — BARU
  // ---------------------------------------------------------
  {
    id: 5,
    category: 'Bristol Chart',
    iconKey: 'baby',
    readMinutes: 3,
    title_id: 'Bristol Chart Versi Anak (mBSFS-C)',
    title_en: 'Bristol Chart for Children (mBSFS-C)',
    summary_id: 'Versi sederhana Bristol Chart yang dirancang khusus untuk anak. Lebih mudah dipahami dan lebih andal saat digunakan bersama anak.',
    summary_en: 'A simplified Bristol Chart designed for children. Easier to understand and more reliable when used together with a child.',
    content_id: `Bristol Stool Chart standar memiliki 7 tipe, yang kadang sulit dibedakan — terutama oleh anak. Untuk itu, peneliti mengembangkan **Modified Bristol Stool Form Scale for Children (mBSFS-C)**: versi 5 tipe yang lebih sederhana.

**Mengapa Versi Anak?**
Penelitian menunjukkan mBSFS-C memberikan kesepakatan yang lebih baik antara tenaga medis dan anak dibanding skala 7-tipe. Dengan menghilangkan tipe yang membingungkan (tipe 3 dan 5 pada skala asli), anak lebih mudah dan lebih konsisten memilih gambar yang sesuai.

**5 Tipe pada mBSFS-C**
- Sangat keras — gumpalan keras terpisah (setara Tipe 1)
- Keras — berbentuk sosis tetapi menggumpal (setara Tipe 2)
- Normal — halus dan lembut seperti sosis/ular (setara Tipe 4)
- Lembek — potongan lembek bertepi tidak rata (setara Tipe 6)
- Cair — sepenuhnya cair (setara Tipe 7)

**Cara Menggunakannya Bersama Anak**
- Tunjukkan gambar dan minta anak menunjuk yang paling mirip
- Gunakan bahasa sederhana ("keras seperti batu" vs "lembek seperti bubur")
- Lakukan setelah anak BAB, selagi ingatannya segar
- Catat hasilnya secara konsisten setiap hari

Versi ini sangat cocok dipakai dalam pencatatan harian di NOWGUT, terutama untuk anak yang sudah bisa diajak berkomunikasi.`,
    content_en: `The standard Bristol Stool Chart has 7 types, which can be hard to distinguish — especially for children. Researchers therefore developed the **Modified Bristol Stool Form Scale for Children (mBSFS-C)**: a simpler 5-type version.

**Why a Children's Version?**
Research shows the mBSFS-C produces better agreement between providers and children than the 7-type scale. By removing the more confusing types (types 3 and 5 on the original), children can choose the matching picture more easily and consistently.

**The 5 Types in mBSFS-C**
- **Very hard** — separate hard lumps (equivalent to Type 1)
- **Hard** — sausage-shaped but lumpy (equivalent to Type 2)
- **Normal** — smooth and soft like a sausage/snake (equivalent to Type 4)
- **Soft** — mushy pieces with ragged edges (equivalent to Type 6)
- **Liquid** — entirely liquid (equivalent to Type 7)

**How to Use It With a Child**
- Show the pictures and ask the child to point to the closest match
- Use simple language ("hard like a rock" vs "soft like porridge")
- Do it right after a bowel movement, while memory is fresh
- Record results consistently each day

This version is well-suited to daily logging in NOWGUT, especially for children who can communicate.`,
    references: [
      { label: 'Comparison of BSFS and Modified Version for Children (2024), PubMed', url: 'https://pubmed.ncbi.nlm.nih.gov/39569880/' },
      { label: 'Lewis SJ, Heaton KW (1997) — skala Bristol asli', url: 'https://pubmed.ncbi.nlm.nih.gov/9299672/' },
    ],
    medicalReview: true,
  },

  // ---------------------------------------------------------
  // 6. Probiotik — BARU
  // ---------------------------------------------------------
  {
    id: 6,
    category: 'Nutrisi',
    iconKey: 'pill',
    readMinutes: 4,
    title_id: 'Probiotik & Kesehatan Usus: Apa Kata Bukti?',
    title_en: 'Probiotics & Gut Health: What Does Evidence Say?',
    summary_id: 'Probiotik sering dibicarakan untuk anak ASD. Pahami apa yang sudah dan belum diketahui ilmu pengetahuan sebelum mempertimbangkannya.',
    summary_en: 'Probiotics are often discussed for children with ASD. Understand what science does and does not yet know before considering them.',
    content_id: `Probiotik — bakteri "baik" untuk usus — sering disebut sebagai cara mendukung kesehatan pencernaan anak ASD. Apa kata bukti ilmiahnya?

**Apa yang Diteliti?**
Anak ASD sering memiliki komposisi mikrobiota usus yang berbeda (disebut "disbiosis"). Karena itu, peneliti mempelajari apakah intervensi yang memodulasi mikrobiota — seperti probiotik, prebiotik, sinbiotik, atau transplantasi mikrobiota — dapat membantu gejala pencernaan.

**Apa Temuannya?**
Tinjauan sistematis terbaru menunjukkan hasil yang menjanjikan namun belum pasti: beberapa studi melaporkan perbaikan gejala GI dan perubahan komposisi mikrobiota, tetapi kualitas dan metode penelitian masih sangat beragam. Bukti belum cukup kuat untuk merekomendasikan jenis atau dosis probiotik tertentu secara umum.

**Yang Perlu Dipahami Caregiver**
- Probiotik bukan "obat" untuk ASD maupun jaminan menyembuhkan masalah GI
- Respons tiap anak bisa berbeda
- Produk probiotik sangat beragam — jenis bakteri, dosis, dan kualitasnya tidak seragam
- Beberapa anak (mis. dengan sistem imun lemah) perlu kehati-hatian khusus

**Langkah Bijak**
Sebelum memberikan suplemen probiotik, **konsultasikan dengan dokter anak.** Sumber probiotik alami dari makanan (seperti yogurt, jika anak tidak intoleran) umumnya lebih aman sebagai langkah awal. Pantau dan catat respons anak jika mencobanya.`,
    content_en: `Probiotics — "good" gut bacteria — are often mentioned as a way to support GI health in children with ASD. What does the evidence say?

**What's Being Studied?**
Children with ASD often have a different gut microbiota composition (called "dysbiosis"). Researchers therefore study whether microbiota-modulating interventions — such as probiotics, prebiotics, synbiotics, or microbiota transplantation — can help digestive symptoms.

**What Are the Findings?**
Recent systematic reviews show promising but uncertain results: some studies report improved GI symptoms and changes in microbiota composition, but study quality and methods vary widely. Evidence is not yet strong enough to recommend a specific type or dose of probiotic in general.

**What Caregivers Should Understand**
- Probiotics are not a "cure" for ASD nor a guarantee of fixing GI problems
- Each child's response can differ
- Probiotic products vary greatly — bacterial strains, doses, and quality are not uniform
- Some children (e.g., with weakened immunity) need special caution

**The Wise Step**
Before giving probiotic supplements, **consult a pediatrician.** Natural food sources of probiotics (like yogurt, if the child is not intolerant) are generally safer as a first step. Monitor and record your child's response if you try them.`,
    references: [
      { label: 'Lu et al. (2025), Autism Research — Gut microbiota modulation meta-analysis', url: 'https://onlinelibrary.wiley.com/doi/10.1002/aur.70091' },
    ],
    medicalReview: true,
  },

  // ---------------------------------------------------------
  // 7. Kapan Harus ke Dokter — BARU (artikel keselamatan)
  // ---------------------------------------------------------
  {
    id: 7,
    category: 'Tips',
    iconKey: 'alert',
    readMinutes: 3,
    title_id: 'Kapan Harus ke Dokter atau IGD?',
    title_en: 'When to See a Doctor or Go to the ER?',
    summary_id: 'Tanda bahaya (red flags) yang menandakan anak perlu segera diperiksa. Simpan dan kenali daftar ini.',
    summary_en: 'Red-flag signs that mean a child needs prompt medical attention. Keep and recognize this list.',
    content_id: `Sebagian besar masalah pencernaan ringan dapat dipantau di rumah. Namun, ada tanda-tanda tertentu yang menandakan anak perlu **segera** diperiksa. Kenali daftar ini.

**Segera ke Dokter / IGD jika anak mengalami:**
- Feses berwarna hitam pekat atau mengandung darah merah segar
- Feses pucat/putih seperti dempul
- Muntah darah atau muntah berwarna hijau
- Nyeri perut hebat yang membuat anak menangis terus atau tidak bisa ditenangkan
- Perut membengkak, keras, dan terasa nyeri saat disentuh
- Tanda dehidrasi: bibir sangat kering, tidak buang air kecil >8 jam, lemas, mata cekung
- Diare berat yang tidak berhenti, terutama disertai demam tinggi
- Tidak BAB disertai muntah dan perut menggembung (kemungkinan sumbatan)

**Perlu Konsultasi (tidak darurat, tetapi jangan ditunda):**
- Sembelit: BAB kurang dari 3x seminggu selama lebih dari 2 minggu
- Diare yang berlangsung lebih dari beberapa hari
- Penurunan berat badan atau nafsu makan yang menetap
- Perubahan pola BAB yang menetap tanpa sebab jelas

**Yang Bisa Anda Siapkan Sebelum ke Dokter**
- Catatan pola BAB (frekuensi, bentuk via Bristol Chart, warna)
- Foto feses bila ada kelainan warna/bentuk
- Daftar makanan terakhir dan obat/suplemen yang dikonsumsi
- Catatan perubahan perilaku yang menyertai

**Penting:** Daftar ini adalah panduan umum, bukan diagnosis. Jika Anda ragu atau merasa ada yang tidak beres pada anak, lebih baik periksakan. Naluri caregiver itu berharga.`,
    content_en: `Most mild digestive problems can be monitored at home. However, certain signs mean a child needs **prompt** medical attention. Know this list.

**Go to a Doctor / ER immediately if the child has:**
- **Deep black** stool or stool containing **fresh red blood**
- **Pale/putty-white** stool
- **Vomiting blood** or green-colored vomit
- **Severe abdominal pain** causing constant crying or that cannot be soothed
- A **swollen, hard abdomen that is painful** to touch
- Signs of **dehydration**: very dry lips, no urination >8 hours, lethargy, sunken eyes
- **Severe diarrhea** that won't stop, especially with high fever
- No bowel movement along with vomiting and a distended belly (possible obstruction)

**Needs Consultation (not emergency, but don't delay):**
- Constipation: fewer than 3 bowel movements per week for more than 2 weeks
- Diarrhea lasting more than a few days
- Persistent weight loss or reduced appetite
- A persistent change in bowel pattern without clear cause

**What to Prepare Before Seeing the Doctor**
- Bowel pattern logs (frequency, form via Bristol Chart, color)
- Photos of stool if there are color/form abnormalities
- A list of recent foods and any medications/supplements taken
- Notes on accompanying behavioral changes

**Important:** This list is general guidance, not a diagnosis. If you are unsure or feel something is wrong with your child, it's better to get them checked. A caregiver's instinct is valuable.`,
    references: [
      { label: 'Nutrients (2022) — GI symptoms in ASD (konteks tanda klinis)', url: 'https://www.mdpi.com/2072-6643/14/7/1471' },
    ],
    medicalReview: true,
  },
];

// Kategori untuk filter (label 'Semua'/'All' ditangani di komponen)
export const CATEGORIES = ['Semua', 'GI & ASD', 'Nutrisi', 'Bristol Chart', 'Tips'];